import { useEffect, useState } from 'react';
import { Copy, KeyRound, Loader2, MessageCircle, RefreshCw, ShieldOff, Unlock } from 'lucide-react';
import {
  criarLicencaAdmin,
  liberarLicencaAdmin,
  listarLicencasAdmin,
  revogarLicencaAdmin,
  SENHA_ADMIN_LICENCA,
  textoWhatsAppChave,
  type LicencaRemota,
} from '../../utils/barbeariaLicenca';

const LS_SENHA = 'carioca-painel-licenca-senha';

interface Props {
  /** NW.exe do vendedor: sem senha na tela + botão WhatsApp. */
  modoNwVendedor?: boolean;
}

export default function BarbeariaPainelLicencas({ modoNwVendedor = false }: Props) {
  const [senha, setSenha] = useState(() => {
    if (modoNwVendedor) return SENHA_ADMIN_LICENCA;
    try {
      return localStorage.getItem(LS_SENHA) || '';
    } catch {
      return '';
    }
  });
  const [logado, setLogado] = useState(modoNwVendedor);
  const [lista, setLista] = useState<LicencaRemota[]>([]);
  const [clienteNome, setClienteNome] = useState('');
  const [nota, setNota] = useState('');
  const [msg, setMsg] = useState('');
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [ultimaChave, setUltimaChave] = useState('');
  const [ultimoCliente, setUltimoCliente] = useState('');

  const carregar = async (s = senha) => {
    setErro('');
    setCarregando(true);
    const r = await listarLicencasAdmin(s);
    setCarregando(false);
    if (!r.ok) {
      setErro(r.erro || 'Não foi possível carregar as chaves (verifique a internet).');
      setLogado(false);
      return;
    }
    setLogado(true);
    setLista(r.licencas || []);
    if (!modoNwVendedor) {
      try {
        localStorage.setItem(LS_SENHA, s);
      } catch {
        /* ignore */
      }
    }
  };

  useEffect(() => {
    void carregar(modoNwVendedor ? SENHA_ADMIN_LICENCA : senha);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modoNwVendedor]);

  const criar = async () => {
    setMsg('');
    setErro('');
    setCarregando(true);
    const r = await criarLicencaAdmin(senha || SENHA_ADMIN_LICENCA, { clienteNome, nota });
    setCarregando(false);
    if (!r.ok || !r.licenca) {
      setErro(r.erro || 'Não foi possível criar');
      return;
    }
    setUltimaChave(r.licenca.chave);
    setUltimoCliente(r.licenca.clienteNome || clienteNome);
    setMsg(`Chave gerada pelo site: ${r.licenca.chave}`);
    setClienteNome('');
    setNota('');
    await carregar();
  };

  const copiar = async (texto: string) => {
    try {
      await navigator.clipboard.writeText(texto);
      setMsg('Chave copiada.');
    } catch {
      setMsg(texto);
    }
  };

  const enviarWhatsApp = (chave: string, nomeCliente?: string) => {
    const texto = textoWhatsAppChave(chave, nomeCliente);
    window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank');
    setMsg('Abrindo WhatsApp com a chave pronta — escolha o contato e envie.');
  };

  const liberar = async (chave: string) => {
    if (!confirm(`Liberar aparelho da chave ${chave}? O cliente poderá ativar em outro celular.`))
      return;
    setCarregando(true);
    const r = await liberarLicencaAdmin(senha || SENHA_ADMIN_LICENCA, chave);
    setCarregando(false);
    if (!r.ok) {
      setErro(r.erro || 'Falha ao liberar');
      return;
    }
    setMsg('Aparelho liberado.');
    await carregar();
  };

  const revogar = async (chave: string) => {
    if (!confirm(`Revogar a chave ${chave}? Ela não poderá mais ser usada.`)) return;
    setCarregando(true);
    const r = await revogarLicencaAdmin(senha || SENHA_ADMIN_LICENCA, chave);
    setCarregando(false);
    if (!r.ok) {
      setErro(r.erro || 'Falha ao revogar');
      return;
    }
    setMsg('Chave revogada.');
    await carregar();
  };

  const statusDe = (l: LicencaRemota) => {
    if (l.revogada) return { label: 'Revogada', cls: 'bg-red-100 text-red-900' };
    if (l.deviceId) return { label: 'Ativa (1 aparelho)', cls: 'bg-emerald-100 text-emerald-900' };
    return { label: 'Aguardando ativação', cls: 'bg-amber-100 text-amber-950' };
  };

  return (
    <div className="mx-auto max-w-3xl space-y-5 py-2">
      <div className="rounded-2xl border-2 border-neutral-800 bg-white p-5">
        <div className="mb-3 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-neutral-900 text-amber-400">
            <KeyRound className="h-5 w-5" />
          </span>
          <div>
            <h1 className="text-lg font-black text-neutral-950">
              {modoNwVendedor ? 'Licenças (vendedor)' : 'Painel de licenças'}
            </h1>
            <p className="text-xs text-neutral-600">
              {modoNwVendedor
                ? 'A chave é gerada na nuvem. Daqui você cria e envia pelo WhatsApp.'
                : 'Só você acessa. Gere a chave e envie ao cliente na venda.'}
            </p>
          </div>
        </div>

        {!logado && !modoNwVendedor ? (
          <div className="space-y-3">
            <label className="block text-xs font-bold text-neutral-800">Senha do painel</label>
            <input
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              className="w-full rounded-xl border-2 border-neutral-400 px-3 py-2.5 text-sm outline-none focus:border-amber-600"
              placeholder="Senha"
              onKeyDown={(e) => {
                if (e.key === 'Enter') void carregar();
              }}
            />
            <button
              type="button"
              onClick={() => void carregar()}
              disabled={carregando || !senha}
              className="inline-flex items-center gap-2 rounded-xl border-2 border-neutral-900 bg-amber-500 px-4 py-2.5 text-sm font-black text-neutral-950 disabled:opacity-50"
            >
              {carregando ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Entrar
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-bold">Nome do cliente (opcional)</label>
                <input
                  value={clienteNome}
                  onChange={(e) => setClienteNome(e.target.value)}
                  className="w-full rounded-xl border-2 border-neutral-400 px-3 py-2 text-sm outline-none focus:border-amber-600"
                  placeholder="Ex.: Barbearia do João"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-bold">Nota interna (opcional)</label>
                <input
                  value={nota}
                  onChange={(e) => setNota(e.target.value)}
                  className="w-full rounded-xl border-2 border-neutral-400 px-3 py-2 text-sm outline-none focus:border-amber-600"
                  placeholder="WhatsApp, cidade…"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void criar()}
                disabled={carregando}
                className="inline-flex items-center gap-2 rounded-xl border-2 border-neutral-900 bg-amber-500 px-4 py-2.5 text-sm font-black text-neutral-950 disabled:opacity-50"
              >
                {carregando ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                Gerar nova chave
              </button>
              <button
                type="button"
                onClick={() => void carregar()}
                disabled={carregando}
                className="inline-flex items-center gap-2 rounded-xl border-2 border-neutral-500 bg-white px-3 py-2.5 text-sm font-bold text-neutral-900"
              >
                <RefreshCw className="h-4 w-4" />
                Atualizar lista
              </button>
            </div>

            {ultimaChave && (
              <div className="flex flex-wrap items-center gap-2 rounded-xl border border-emerald-700 bg-emerald-50 px-3 py-2">
                <code className="font-mono text-sm font-bold text-emerald-950">{ultimaChave}</code>
                <button
                  type="button"
                  onClick={() => void copiar(ultimaChave)}
                  className="inline-flex items-center gap-1 rounded-lg border border-emerald-800 bg-white px-2 py-1 text-xs font-bold"
                >
                  <Copy className="h-3.5 w-3.5" />
                  Copiar
                </button>
                <button
                  type="button"
                  onClick={() => enviarWhatsApp(ultimaChave, ultimoCliente)}
                  className="inline-flex items-center gap-1 rounded-lg border border-emerald-800 bg-emerald-600 px-2 py-1 text-xs font-bold text-white"
                >
                  <MessageCircle className="h-3.5 w-3.5" />
                  Enviar no WhatsApp
                </button>
              </div>
            )}
          </div>
        )}

        {(msg || erro) && (
          <p
            className={`mt-3 rounded-xl px-3 py-2 text-xs font-medium ${
              erro
                ? 'border border-red-700 bg-red-50 text-red-900'
                : 'border border-neutral-400 bg-neutral-50 text-neutral-800'
            }`}
          >
            {erro || msg}
          </p>
        )}
      </div>

      {logado && (
        <div className="space-y-2">
          <h2 className="text-sm font-black text-neutral-900">Chaves ({lista.length})</h2>
          {lista.length === 0 && (
            <p className="text-sm text-neutral-600">Nenhuma chave ainda. Gere a primeira acima.</p>
          )}
          <ul className="space-y-2">
            {lista.map((l) => {
              const st = statusDe(l);
              return (
                <li
                  key={l.chave}
                  className="rounded-xl border-2 border-neutral-300 bg-white px-3 py-3 text-sm"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <code className="font-mono text-sm font-bold text-neutral-950">{l.chave}</code>
                      <p className="mt-0.5 text-xs text-neutral-600">
                        {l.clienteNome || '—'} {l.nota ? `· ${l.nota}` : ''}
                      </p>
                      <p className="text-[10px] text-neutral-500">
                        Criada {new Date(l.criadaEm).toLocaleString('pt-BR')}
                        {l.ativadaEm
                          ? ` · Ativada ${new Date(l.ativadaEm).toLocaleString('pt-BR')}`
                          : ''}
                      </p>
                    </div>
                    <span className={`rounded-lg px-2 py-1 text-[10px] font-bold ${st.cls}`}>
                      {st.label}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => void copiar(l.chave)}
                      className="inline-flex items-center gap-1 rounded-lg border border-neutral-400 px-2 py-1 text-xs font-bold"
                    >
                      <Copy className="h-3 w-3" />
                      Copiar
                    </button>
                    <button
                      type="button"
                      onClick={() => enviarWhatsApp(l.chave, l.clienteNome)}
                      className="inline-flex items-center gap-1 rounded-lg border border-emerald-700 bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-950"
                    >
                      <MessageCircle className="h-3 w-3" />
                      WhatsApp
                    </button>
                    {l.deviceId && !l.revogada && (
                      <button
                        type="button"
                        onClick={() => void liberar(l.chave)}
                        className="inline-flex items-center gap-1 rounded-lg border border-emerald-700 px-2 py-1 text-xs font-bold text-emerald-900"
                      >
                        <Unlock className="h-3 w-3" />
                        Liberar aparelho
                      </button>
                    )}
                    {!l.revogada && (
                      <button
                        type="button"
                        onClick={() => void revogar(l.chave)}
                        className="inline-flex items-center gap-1 rounded-lg border border-red-700 px-2 py-1 text-xs font-bold text-red-900"
                      >
                        <ShieldOff className="h-3 w-3" />
                        Revogar
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
